import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useParams } from 'react-router-dom';
import {
  makeStyles,
  mergeClasses,
  tokens,
  typographyStyles,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuGroup,
  MenuGroupHeader,
  MenuDivider,
  Avatar,
  Button,
} from '@fluentui/react-components';
import {
  SignOutRegular,
  SettingsRegular,
  BookOpenRegular,
  MegaphoneRegular,
  BugRegular,
  LightbulbRegular,
  ArrowSwapRegular,
  WeatherSunnyRegular,
  WeatherMoonRegular,
} from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { OttoLogo } from '@/components/OttoLogo';
import { useAuth } from '@/auth';
import { NotificationPanel } from '@/components/shared/NotificationPanel';
import { ToastOverlay } from '@/components/shared/ToastOverlay';
import { HeaderNavigation } from '@/components/shared/HeaderNavigation/HeaderNavigation';
import { TenantSwitchDialog } from '@/components/TenantDropdown';
import { GlobalNavPalette, PaletteExtensionsProvider } from '@/components/GlobalNavPalette';
import { SettingsModal } from '@/components/SettingsModal';
import { JumpToChip } from '@/components/shared/JumpToChip';
import { KeyboardShortcutsDialog } from '@/components/shared/KeyboardShortcutsDialog';
import { useKeyboardShortcutsHelp } from '@/hooks/useKeyboardShortcutsHelp';
import { useSettingsModal, useSettingsModalShortcut } from '@/hooks/useSettingsModal';
import { useSidebarToggleShortcut } from '@/hooks/useSidebarToggleShortcut';

import { useRuntimeManifestSync } from '@/hooks/useRuntimeManifestSync';
import { setLogicEndpoint } from '@/services/endpoints';
import { getProjectLocation } from '@/utils/system';
import { useUserPhoto } from '@/hooks/useUserPhoto';
import { getShortcutLabel } from '@/utils/input';
import { useAppStore } from '@/stores/appStore';
import { useTenants } from '@/hooks/useAzureResources';
import { LayoutProvider } from '@/contexts';

const useStyles = makeStyles({
  skipNav: {
    position: 'absolute',
    top: '-100px',
    left: tokens.spacingHorizontalL,
    zIndex: 1000,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    border: `2px solid ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    textDecoration: 'none',
    ':focus': {
      top: '8px',
    },
    ':focus-visible': {
      top: '8px',
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '2px',
    },
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground2,
    overflow: 'hidden',
  },
  header: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
    position: 'sticky',
    top: 0,
    zIndex: 100,
    flexShrink: 0,
    transitionProperty: 'box-shadow',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  headerScrolled: {
    boxShadow: tokens.shadow4,
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    height: '48px',
    paddingInline: tokens.spacingHorizontalL,
    gap: tokens.spacingHorizontalL,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalMNudge,
    textDecoration: 'none',
    color: tokens.colorNeutralForeground1,
  },
  logoIcon: {
    width: '28px',
    height: '28px',
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightBold,
  },
  divider: {
    width: '1px',
    height: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralStroke1,
  },
  spacer: {
    flex: 1,
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  userButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingInline: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalXXS,
    minWidth: 'auto',
    height: 'auto',
  },
  // The brand-coloured avatar paints white initials on colorBrandBackgroundStatic
  // (#5B6CF0), which lands at 4.33:1 — just under the WCAG AA 4.5:1 text
  // threshold. Re-point that token to the darker colorBrandBackground (#3350cc,
  // ~6.6:1) within the avatar wrapper, so Fluent's own initials rule resolves to
  // an AA-compliant, on-brand colour. Scoped to the wrapper, so nothing else is
  // affected; a user photo, when present, covers the initials entirely.
  accessibleAvatarWrap: {
    display: 'inline-flex',
    '--colorBrandBackgroundStatic': tokens.colorBrandBackground,
  },
  // ── Two-line identity stack next to the avatar ────────────────────────
  // Avatar leads, with a stacked name (Caption 1 Strong) over tenant
  // (Caption 2) to its right. Text is left-aligned to sit flush against
  // the avatar. Hidden on narrow viewports; the tenant remains
  // discoverable via the menu's "Tenant" section.
  identityStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
    '@media (max-width: 640px)': {
      display: 'none',
    },
  },
  identityName: {
    ...typographyStyles.caption1Strong,
    color: tokens.colorNeutralForeground1,
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  identityTenant: {
    ...typographyStyles.caption2,
    color: tokens.colorNeutralForeground3,
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // ── Profile popover header ─────────────────────────────────────────
  // Avatar + identity column (name + email) + tenant-switch icon
  // button, separated from the menu list below with a hairline.
  popoverHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingInline: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    // Margin (not padding) so the gap sits OUTSIDE the hairline — i.e.
    // between the header and the MenuList — visually matching the divider
    // + group-header spacing that follows the first MenuItem below.
    marginBlockEnd: tokens.spacingVerticalSNudge,
  },
  popoverIdentity: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    gap: tokens.spacingVerticalXXS,
  },
  popoverName: {
    ...typographyStyles.body1Strong,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  popoverEmail: {
    ...typographyStyles.caption1,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Compact icon button to match NotificationPanel sizing.
  // Opens the docs site in a new tab; aria-label disambiguates for AT.
  docsButton: {
    minWidth: '32px',
    height: '32px',
    paddingInline: tokens.spacingHorizontalXS,
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    minHeight: 0,
  },
  main: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
});

export const RootLayout = () => {
  const styles = useStyles();
  const { account, logout } = useAuth();
  const selectedTenantId = useAppStore((s) => s.selectedTenantId);
  const setSelectedTenant = useAppStore((s) => s.setSelectedTenant);
  const theme = useAppStore((s) => s.theme);
  const toggleTheme = useAppStore((s) => s.toggleTheme);
  const queryClient = useQueryClient();
  const [tenantSwitchOpen, setTenantSwitchOpen] = useState(false);
  const { isOpen: shortcutsOpen, open: openShortcuts, close: closeShortcuts } = useKeyboardShortcutsHelp();
  const {
    isOpen: settingsOpen,
    initialSection: settingsInitialSection,
    open: openSettings,
    close: closeSettings,
  } = useSettingsModal();
  useSettingsModalShortcut();
  useSidebarToggleShortcut();
  const [isScrolled, setIsScrolled] = useState(false);
  const userPhotoUrl = useUserPhoto();

  // Open Switch tenant ON TOP of the Settings modal — do NOT close
  // Settings synchronously. Fluent Dialog renders into a portal/Layer
  // so the tenant surface stacks above Settings, and Cancel/Esc returns
  // the user to Settings where they left it. If the user actually
  // picks a tenant, the dialog calls `onSwitched` (below) which closes
  // Settings — `navigate()` alone only swaps the route Outlet, so
  // Settings would otherwise stay open behind the (now-closed) dialog.
  const handleSwitchTenantFromSettings = useCallback(() => {
    setTenantSwitchOpen(true);
  }, []);
  // After a successful tenant switch, close any surfaces that were
  // stacked above the dialog (Settings) so the user lands cleanly on
  // the new tenant's home route.
  const handleTenantSwitched = useCallback(() => {
    closeSettings();
  }, [closeSettings]);
  // Open shortcuts ON TOP of the Settings modal — do NOT close Settings.
  // Fluent Dialog renders into a portal/Layer, so the shortcuts surface
  // stacks above the Settings surface; closing shortcuts (Esc or Close)
  // returns the user to Settings exactly where they left it.
  const handleOpenShortcutsFromSettings = useCallback(() => {
    openShortcuts();
  }, [openShortcuts]);

  // Active tenant display name — surfaced as a second line under the
  // user name in the header (Azure Portal pattern). We show it whenever
  // the tenant is known, including for single-tenant users, mirroring
  // Azure Portal's "always acknowledge which tenant you're in" stance.
  // The Switch tenant menu item is still gated on multi-tenant users
  // below, so single-tenant users get acknowledgement without a
  // meaningless switcher.
  const { data: tenants } = useTenants();
  // Auto-select tenant on first load: home tenant (account.tenantId) >
  // first tenant. Mirrors the legacy TenantMenuSection behavior so the
  // header shows the active tenant name immediately, even before the
  // user opens the avatar menu. URL-driven selection is handled
  // separately by TenantLayout for routes scoped under /:tenantId.
  useEffect(() => {
    if (selectedTenantId || !tenants?.length) return;
    const homeTenant = account?.tenantId
      ? tenants.find((t) => t.tenantId === account.tenantId)
      : undefined;
    const defaultTenant = homeTenant ?? tenants[0];
    if (defaultTenant) {
      setSelectedTenant(defaultTenant.tenantId);
    }
  }, [tenants, selectedTenantId, setSelectedTenant, account?.tenantId]);
  const activeTenantName =
    tenants && selectedTenantId
      ? tenants.find((t) => t.tenantId === selectedTenantId)?.displayName ?? null
      : null;

  // Sync logic endpoint from URL location param
  const { locationName } = useParams<{ tenantId: string; locationName: string }>();
  const prevLocationRef = useRef('');

  useEffect(() => {
    if (locationName && locationName !== prevLocationRef.current) {
      const prevLoc = prevLocationRef.current;
      prevLocationRef.current = locationName;
      setLogicEndpoint(getProjectLocation(locationName));
      if (prevLoc) {
        // Location actually changed (not first mount) — invalidate location-scoped caches
        queryClient.invalidateQueries();
      }
    }
   
  }, [locationName, queryClient]);

  // Sync runtime manifests for connector operations
  useRuntimeManifestSync();

  useEffect(() => {
    const scrollEl = document.getElementById('main-content');
    if (!scrollEl) return;
    const handleScroll = () => setIsScrolled(scrollEl.scrollTop > 0);
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, []);

  const userInitials = account?.name
    ? account.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <LayoutProvider>
      <PaletteExtensionsProvider>
        <div className={styles.layout}>
          <a href="#main-content" className={styles.skipNav}>
            Skip to main content
          </a>
          <header className={mergeClasses(styles.header, isScrolled && styles.headerScrolled)}>
            <nav className={styles.nav} aria-label="Global">
              <NavLink to="/" className={styles.logo} aria-label="Automation Home">
                <OttoLogo size={24} />
              </NavLink>
              {locationName && <div className={styles.divider} aria-hidden="true" />}
              {locationName && <HeaderNavigation />}
              <div className={styles.spacer} />
              <JumpToChip />
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    appearance="subtle"
                    icon={<MegaphoneRegular />}
                    aria-label="Send feedback"
                    title="Feedback"
                    className={styles.docsButton}
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem
                      icon={<BugRegular />}
                      onClick={() => window.open('https://github.com/Azure/Logic-Apps-Automation/issues/new?template=bug_report.yml', '_blank', 'noopener,noreferrer')}
                    >
                      Report a bug
                    </MenuItem>
                    <MenuItem
                      icon={<LightbulbRegular />}
                      onClick={() => window.open('https://github.com/Azure/Logic-Apps-Automation/issues/new?template=feature_request.yml', '_blank', 'noopener,noreferrer')}
                    >
                      Request a feature
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
              <Button
                as="a"
                href="https://aka.ms/otto-docs"
                target="_blank"
                rel="noopener noreferrer"
                appearance="subtle"
                icon={<BookOpenRegular />}
                aria-label="Documentation (opens in a new tab)"
                title="Documentation"
                className={styles.docsButton}
              />
              <NotificationPanel />
              <div className={styles.divider} aria-hidden="true" />
              {account && (
                <div className={styles.userSection}>
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button
                        appearance="subtle"
                        className={styles.userButton}
                        aria-label={
                          activeTenantName
                            ? `${account.name ?? account.username ?? 'User'} (${activeTenantName}), open user menu`
                            : `${account.name ?? account.username ?? 'User'} user menu`
                        }
                      >
                        <span className={styles.accessibleAvatarWrap}>
                          <Avatar
                            name={account.name ?? account.username}
                            initials={userInitials}
                            size={28}
                            color="brand"
                            {...(userPhotoUrl ? { image: { src: userPhotoUrl } } : {})}
                          />
                        </span>
                        <span className={styles.identityStack} aria-hidden="true">
                          <span
                            className={styles.identityName}
                            title={account.name ?? account.username ?? ''}
                          >
                            {account.name ?? account.username}
                          </span>
                          {activeTenantName && (
                            <span
                              className={styles.identityTenant}
                              title={activeTenantName}
                            >
                              {activeTenantName}
                            </span>
                          )}
                        </span>
                      </Button>
                    </MenuTrigger>
                    <MenuPopover
                      aria-label={
                        account.name && account.username && account.name !== account.username
                          ? `Signed in as ${account.name}, ${account.username}`
                          : `Signed in as ${account.name ?? account.username ?? ''}`
                      }
                    >
                      <div className={styles.popoverHeader}>
                        <div aria-hidden="true">
                          <span className={styles.accessibleAvatarWrap}>
                            <Avatar
                              name={account.name ?? account.username}
                              initials={userInitials}
                              size={40}
                              color="brand"
                              {...(userPhotoUrl ? { image: { src: userPhotoUrl } } : {})}
                            />
                          </span>
                        </div>
                        <div className={styles.popoverIdentity}>
                          <span
                            className={styles.popoverName}
                            title={account.name ?? account.username ?? ''}
                          >
                            {account.name ?? account.username}
                          </span>
                          {account.username && (
                            <span className={styles.popoverEmail} title={account.username}>
                              {account.username}
                            </span>
                          )}
                        </div>
                      </div>
                      <MenuList>
                        <MenuItem
                          icon={<SettingsRegular />}
                          onClick={() => openSettings()}
                          secondaryContent={getShortcutLabel('openSettings')}
                        >
                          Account settings
                        </MenuItem>
                        <MenuDivider />
                        <MenuGroup>
                          <MenuGroupHeader>Preferences</MenuGroupHeader>
                          {tenants && tenants.length > 1 && (
                            <MenuItem
                              icon={<ArrowSwapRegular />}
                              onClick={() => setTenantSwitchOpen(true)}
                            >
                              Switch tenant
                            </MenuItem>
                          )}
                          <MenuItem
                            icon={theme === 'dark' ? <WeatherMoonRegular /> : <WeatherSunnyRegular />}
                            onClick={() => toggleTheme()}
                          >
                            Theme
                          </MenuItem>
                        </MenuGroup>
                        <MenuDivider />
                        <MenuItem icon={<SignOutRegular />} onClick={() => logout()}>
                          Sign out
                        </MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
              )}
              <SettingsModal
                open={settingsOpen}
                initialSection={settingsInitialSection}
                onClose={closeSettings}
                onSwitchTenantClick={handleSwitchTenantFromSettings}
                onOpenShortcutsDialog={handleOpenShortcutsFromSettings}
              />
              <TenantSwitchDialog
                open={tenantSwitchOpen}
                onClose={() => setTenantSwitchOpen(false)}
                onSwitched={handleTenantSwitched}
              />
              <KeyboardShortcutsDialog
                open={shortcutsOpen}
                onClose={closeShortcuts}
              />
            </nav>
          </header>
          <div className={styles.body}>
            <main id="main-content" tabIndex={-1} className={styles.main} aria-label="Main content">
              <Outlet />
            </main>
          </div>
        </div>
        <ToastOverlay />
        <GlobalNavPalette />
      </PaletteExtensionsProvider>
    </LayoutProvider>
  );
};
